import { type FC } from "react";
import { Badge } from "@/shared/components/ui/badge";
import { InsightsCard, InsightsDividerList, InsightsDividerRow, formatDate } from "./InsightsCard";
import { useCitation, getCitationForField } from "./CitationContext";

interface AmendmentInformation {
  amendment_number?: number;
  amendment_type?: "amendment" | "restatement" | "amendment_and_restatement";
  execution_date?: string;
  execution_date_raw?: string;
  state_executed?: string;
  trust_name?: string;
  original_trust_date?: string;
  grantor_name?: string;
  revokes_prior_amendments?: boolean;
}

interface AmendmentChange {
  article_or_section?: string;
  change_type?: "added" | "removed" | "modified" | "replaced";
  description?: string;
  affected_parties?: string[];
}

interface TrusteeChange {
  name?: string;
  change_type?: "added" | "removed" | "modified" | "replaced";
  type?: string;
  role?: string;
  is_primary?: boolean;
  order?: number;
  relationship?: string;
}

interface BeneficiaryChange {
  name?: string;
  change_type?: "added" | "removed" | "modified" | "replaced";
  beneficiary_type?: string;
  relationship?: string;
  percentage?: number;
  distribution_conditions?: string;
}

interface PropertyChange {
  asset_description?: string;
  asset_type?: string;
  property_address?: string;
  change_type?: "added" | "removed" | "modified";
}

export interface TrustAmendmentExtraction {
  amendment_information?: AmendmentInformation;
  scope?: string[];
  changes?: AmendmentChange[];
  trustee_changes?: TrusteeChange[];
  beneficiary_changes?: BeneficiaryChange[];
  property_changes?: PropertyChange[];
}

const ORDINAL_LABELS: Record<number, string> = {
  1: "First",
  2: "Second",
  3: "Third",
  4: "Fourth",
  5: "Fifth",
  6: "Sixth",
  7: "Seventh",
  8: "Eighth",
  9: "Ninth",
  10: "Tenth",
};

function getOrdinalSuffix(n: number): string {
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 13) return "th";
  const mod10 = n % 10;
  if (mod10 === 1) return "st";
  if (mod10 === 2) return "nd";
  if (mod10 === 3) return "rd";
  return "th";
}

const AMENDMENT_TYPE_LABELS: Record<string, string> = {
  amendment: "Amendment",
  restatement: "Restatement",
  amendment_and_restatement: "Amendment and Restatement",
};

const CHANGE_TYPE_LABELS: Record<string, string> = {
  added: "Added",
  removed: "Removed",
  modified: "Modified",
  replaced: "Replaced",
};

const SCOPE_LABELS: Record<string, string> = {
  trustee: "Trustee",
  successor_trustee: "Successor Trustee",
  beneficiary: "Beneficiary",
  distribution: "Distribution",
  property: "Property",
  administrative: "Administrative",
  full_restatement: "Full Restatement",
};

const CHANGE_TYPE_BADGE_CLASSES: Record<string, string> = {
  added: "bg-teal-50 text-teal-700 border-teal-200",
  removed: "bg-red-50 text-red-700 border-red-200",
  modified: "bg-amber-50 text-amber-700 border-amber-200",
  replaced: "bg-blue-50 text-blue-700 border-blue-200",
};

interface TrustAmendmentInsightsProps {
  data: TrustAmendmentExtraction;
  citations?: Record<string, unknown> | null;
}

export const TrustAmendmentInsights: FC<TrustAmendmentInsightsProps> = ({ data, citations }) => {
  const { showCitation } = useCitation();

  const handleFieldClick = (fieldPath: string) => {
    const citation = getCitationForField(citations, fieldPath);
    if (citation) showCitation(citation);
  };

  const {
    amendment_information,
    scope,
    changes,
    trustee_changes,
    beneficiary_changes,
    property_changes,
  } = data;

  const typeLabel = amendment_information?.amendment_type
    ? AMENDMENT_TYPE_LABELS[amendment_information.amendment_type]
    : "Trust Amendment";
  const ordinal = amendment_information?.amendment_number
    ? (ORDINAL_LABELS[amendment_information.amendment_number] ?? `${amendment_information.amendment_number}${getOrdinalSuffix(amendment_information.amendment_number)}`)
    : null;
  const amendmentLabel = ordinal ? `${ordinal} ${typeLabel}` : typeLabel;

  return (
    <div className="px-6 pb-6 space-y-4">
      {amendment_information && (
        <InsightsCard title="Amendment details" noPadding>
          <InsightsDividerList>
            <InsightsDividerRow
              label="Type"
              value={amendmentLabel}
              onClick={
                getCitationForField(citations, "amendment_information.amendment_type")
                  ? () => handleFieldClick("amendment_information.amendment_type")
                  : undefined
              }
            />
            {amendment_information.execution_date && (
              <InsightsDividerRow
                label="Execution Date"
                value={formatDate(amendment_information.execution_date)}
                onClick={
                  getCitationForField(citations, "amendment_information.execution_date")
                    ? () => handleFieldClick("amendment_information.execution_date")
                    : undefined
                }
              />
            )}
            {amendment_information.state_executed && (
              <InsightsDividerRow
                label="State"
                value={amendment_information.state_executed}
                onClick={
                  getCitationForField(citations, "amendment_information.state_executed")
                    ? () => handleFieldClick("amendment_information.state_executed")
                    : undefined
                }
              />
            )}
            {amendment_information.grantor_name && (
              <InsightsDividerRow
                label="Grantor"
                value={amendment_information.grantor_name}
                onClick={
                  getCitationForField(citations, "amendment_information.grantor_name")
                    ? () => handleFieldClick("amendment_information.grantor_name")
                    : undefined
                }
              />
            )}
            {typeof amendment_information.revokes_prior_amendments === "boolean" && (
              <InsightsDividerRow
                label="Revokes Prior Amendments"
                value={amendment_information.revokes_prior_amendments ? "Yes" : "No"}
                onClick={
                  getCitationForField(citations, "amendment_information.revokes_prior_amendments")
                    ? () => handleFieldClick("amendment_information.revokes_prior_amendments")
                    : undefined
                }
              />
            )}
          </InsightsDividerList>
        </InsightsCard>
      )}

      {(amendment_information?.trust_name || amendment_information?.original_trust_date) && (
        <InsightsCard title="Parent trust" noPadding>
          <InsightsDividerList>
            {amendment_information.trust_name && (
              <InsightsDividerRow
                label="Trust Name"
                value={amendment_information.trust_name}
                onClick={
                  getCitationForField(citations, "amendment_information.trust_name")
                    ? () => handleFieldClick("amendment_information.trust_name")
                    : undefined
                }
              />
            )}
            {amendment_information.original_trust_date && (
              <InsightsDividerRow
                label="Original Trust Date"
                value={formatDate(amendment_information.original_trust_date)}
                onClick={
                  getCitationForField(citations, "amendment_information.original_trust_date")
                    ? () => handleFieldClick("amendment_information.original_trust_date")
                    : undefined
                }
              />
            )}
          </InsightsDividerList>
        </InsightsCard>
      )}

      {scope && scope.length > 0 && (
        <InsightsCard title="Scope">
          <div className="flex flex-wrap gap-2">
            {scope.map((item) => (
              <Badge key={item} variant="outline" className="bg-stone-50 text-stone-700 border-stone-200">
                {SCOPE_LABELS[item] ?? item}
              </Badge>
            ))}
          </div>
        </InsightsCard>
      )}

      {changes && changes.length > 0 && (
        <InsightsCard title="Changes" noPadding>
          <InsightsDividerList>
            {changes.map((change, index) => (
              <div
                key={`change-${index}`}
                className={`py-3 px-4 space-y-1.5${getCitationForField(citations, `changes.${index}.description`) ? " cursor-pointer hover:bg-subtle transition-colors" : ""}`}
                onClick={
                  getCitationForField(citations, `changes.${index}.description`)
                    ? () => handleFieldClick(`changes.${index}.description`)
                    : undefined
                }
                role={getCitationForField(citations, `changes.${index}.description`) ? "button" : undefined}
                tabIndex={getCitationForField(citations, `changes.${index}.description`) ? 0 : undefined}
                onKeyDown={
                  getCitationForField(citations, `changes.${index}.description`)
                    ? (e) => {
                        if (e.key === "Enter") handleFieldClick(`changes.${index}.description`);
                      }
                    : undefined
                }
              >
                <div className="flex items-start justify-between gap-2">
                  <span className="text-sm font-medium text-primary inline-flex items-center">
                    {change.article_or_section ?? `Change ${index + 1}`}
                    {getCitationForField(citations, `changes.${index}.description`) && (
                      <span className="ml-1.5 w-1.5 h-1.5 rounded-full bg-green-300 shrink-0" />
                    )}
                  </span>
                  {change.change_type && (
                    <Badge
                      variant="outline"
                      className={CHANGE_TYPE_BADGE_CLASSES[change.change_type] ?? ""}
                    >
                      {CHANGE_TYPE_LABELS[change.change_type] ?? change.change_type}
                    </Badge>
                  )}
                </div>
                {change.description && (
                  <p className="text-sm text-secondary">{change.description}</p>
                )}
                {change.affected_parties && change.affected_parties.length > 0 && (
                  <p className="text-sm text-tertiary">
                    Affects: {change.affected_parties.join(", ")}
                  </p>
                )}
              </div>
            ))}
          </InsightsDividerList>
        </InsightsCard>
      )}

      {trustee_changes && trustee_changes.length > 0 && (
        <InsightsCard title="Trustee changes" noPadding>
          <InsightsDividerList>
            {trustee_changes.map((trustee, index) => {
              const roleLabel = trustee.role === "successor_trustee"
                ? `${trustee.order ? `#${trustee.order}` : ""} Successor Trustee`
                : trustee.is_primary === true
                  ? "Primary Trustee"
                  : trustee.type === "co-trustee" || trustee.role === "co-trustee"
                    ? "Co-Trustee"
                    : "Trustee";
              const changeLabel = trustee.change_type ? CHANGE_TYPE_LABELS[trustee.change_type] : null;
              return (
              <InsightsDividerRow
                key={`trustee-change-${index}`}
                label={changeLabel ? `${changeLabel} - ${roleLabel}` : roleLabel}
                value={[trustee.name, trustee.relationship ? `(${trustee.relationship})` : undefined]
                  .filter(Boolean)
                  .join(" ")}
                onClick={
                  getCitationForField(citations, `trustee_changes.${index}.name`)
                    ? () => handleFieldClick(`trustee_changes.${index}.name`)
                    : undefined
                }
              />
              );
            })}
          </InsightsDividerList>
        </InsightsCard>
      )}

      {beneficiary_changes && beneficiary_changes.length > 0 && (
        <InsightsCard title="Beneficiary changes" noPadding>
          <InsightsDividerList>
            {beneficiary_changes.map((beneficiary, index) => {
              const valueParts = [
                beneficiary.name,
                beneficiary.relationship ? `(${beneficiary.relationship})` : undefined,
                typeof beneficiary.percentage === "number" ? `- ${beneficiary.percentage}%` : undefined,
              ].filter(Boolean);

              const benTypeLabel = beneficiary.beneficiary_type === "contingent"
                ? "Contingent Beneficiary"
                : beneficiary.beneficiary_type === "remainder"
                  ? "Remainder Beneficiary"
                  : "Beneficiary";
              const benChangeLabel = beneficiary.change_type ? CHANGE_TYPE_LABELS[beneficiary.change_type] : null;

              return (
                <InsightsDividerRow
                  key={`beneficiary-change-${index}`}
                  label={benChangeLabel ? `${benChangeLabel} - ${benTypeLabel}` : benTypeLabel}
                  value={valueParts.join(" ")}
                  onClick={
                    getCitationForField(citations, `beneficiary_changes.${index}.name`)
                      ? () => handleFieldClick(`beneficiary_changes.${index}.name`)
                      : undefined
                  }
                />
              );
            })}
          </InsightsDividerList>
        </InsightsCard>
      )}

      {property_changes && property_changes.length > 0 && (
        <InsightsCard title="Property changes" noPadding>
          <InsightsDividerList>
            {property_changes.map((property, index) => (
              <div
                key={`property-change-${index}`}
                className={`py-3 px-4 space-y-1${getCitationForField(citations, `property_changes.${index}.asset_description`) ? " cursor-pointer hover:bg-subtle transition-colors" : ""}`}
                onClick={
                  getCitationForField(citations, `property_changes.${index}.asset_description`)
                    ? () => handleFieldClick(`property_changes.${index}.asset_description`)
                    : undefined
                }
                role={getCitationForField(citations, `property_changes.${index}.asset_description`) ? "button" : undefined}
                tabIndex={getCitationForField(citations, `property_changes.${index}.asset_description`) ? 0 : undefined}
                onKeyDown={
                  getCitationForField(citations, `property_changes.${index}.asset_description`)
                    ? (e) => {
                        if (e.key === "Enter")
                          handleFieldClick(`property_changes.${index}.asset_description`);
                      }
                    : undefined
                }
              >
                <div className="flex items-start justify-between gap-2">
                  <span className="text-sm font-medium text-primary inline-flex items-center">
                    {property.asset_description ?? property.asset_type ?? "Asset"}
                    {getCitationForField(
                      citations,
                      `property_changes.${index}.asset_description`,
                    ) && (
                      <span className="ml-1.5 w-1.5 h-1.5 rounded-full bg-green-300 shrink-0" />
                    )}
                  </span>
                  {property.change_type && (
                    <Badge
                      variant="outline"
                      className={CHANGE_TYPE_BADGE_CLASSES[property.change_type] ?? ""}
                    >
                      {CHANGE_TYPE_LABELS[property.change_type] ?? property.change_type}
                    </Badge>
                  )}
                </div>
                {property.property_address && (
                  <p className="text-sm text-secondary">{property.property_address}</p>
                )}
              </div>
            ))}
          </InsightsDividerList>
        </InsightsCard>
      )}
    </div>
  );
};
